import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Segmented } from "@/components/atoms/Segmented";
import { Pill } from "@/components/atoms/Pill";
import { fmtUSD } from "@/components/atoms/format";
import { useMid } from "@/components/market/useMid";

type OrderType = "market" | "limit";
type TimeInForce = "gtc" | "ioc" | "fok" | "gtd";

const QUICK_SIZES = [10, 25, 50, 100, 250];

export function TradePanel() {
  const panel = useStore((s) => s.tradePanel);
  const close = useStore((s) => s.closeTrade);
  const showToast = useStore((s) => s.showToast);
  const userId = useStore((s) => s.userId);
  const setWalletOpen = useStore((s) => s.setWalletOpen);

  const open = panel !== null;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={close}
        />
      )}
      <aside
        className={`fixed right-0 top-0 z-50 flex h-screen w-[420px] max-w-[92vw] flex-col border-l border-line bg-bg-1 shadow-[var(--shadow-card)] transition-transform duration-[360ms] ease-[cubic-bezier(.2,.7,.2,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {panel && (
          <TradePanelInner
            key={panel.market.id + panel.outcomeId}
            panel={panel}
            close={close}
            onCompleted={(msg) =>
              showToast({ kind: "success", message: msg })
            }
            authReady={!!userId}
            requireAuth={() => setWalletOpen(true)}
          />
        )}
      </aside>
    </>
  );
}

interface InnerProps {
  panel: NonNullable<ReturnType<typeof useStore.getState>["tradePanel"]>;
  close: () => void;
  onCompleted: (msg: string) => void;
  authReady: boolean;
  requireAuth: () => void;
}

function TradePanelInner({
  panel,
  close,
  onCompleted,
  authReady,
  requireAuth,
}: InnerProps) {
  const { market, outcomeId, side } = panel;
  const mid = useMid(market.slug, 3000);
  const outcome = market.outcomes.find((o) => o.id === outcomeId) ?? market.outcomes[0];

  const [tab, setTab] = useState<"buy" | "sell">(side);
  const [orderType, setOrderType] = useState<OrderType>("limit");
  const [tif, setTif] = useState<TimeInForce>("gtc");
  const [size, setSize] = useState(10);
  const [limitPct, setLimitPct] = useState(Math.round(mid * 100));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Aggiorna il limit slider quando arriva un nuovo mid e l'utente non ha toccato il valore.
    setLimitPct(Math.round(mid * 100));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  const usePrice = orderType === "market" ? mid : Math.max(0.001, Math.min(0.999, limitPct / 100));
  const notional = usePrice * size;
  const fee = notional * 0.02;
  const payout = size; // 1 share = 1 USDC se vince
  const profit = payout - notional;
  const roi = notional > 0 ? (profit / notional) * 100 : 0;

  const submit = async () => {
    if (!authReady) {
      requireAuth();
      return;
    }
    setSubmitting(true);
    setError(null);
    // Round price a 3 decimali per rispettare il tick del backend (0.001).
    const priceStr = (Math.round(usePrice * 1000) / 1000).toFixed(3);
    // Size deve essere multiplo di 0.01 — round.
    const sizeStr = (Math.round(size * 100) / 100).toFixed(2);
    const { data, error: err } = await api.POST("/v1/orders", {
      body: {
        market_id: market.id,
        outcome_id: outcome.id,
        side: tab,
        order_type: orderType,
        time_in_force: tif,
        price: priceStr,
        size: sizeStr,
        idempotency_key: `panel-${market.id}-${Date.now()}`,
      },
    });
    setSubmitting(false);
    if (data) {
      onCompleted(
        `${tab === "buy" ? "Buy" : "Sell"} ${size} ${outcome.label} @ ${priceStr} placed`,
      );
      close();
    } else if (err) {
      const msg =
        typeof err === "object" && err && "message" in err
          ? String((err as { message: unknown }).message)
          : "Order rejected";
      setError(msg);
    }
  };

  return (
    <>
      <header className="flex items-center gap-3 border-b border-line p-4">
        <span
          className="h-2 w-2 rounded-full bg-accent"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-wider text-muted">
            {market.category}
          </div>
          <h2 className="line-clamp-2 text-[14px] font-semibold leading-tight">
            {market.title}
          </h2>
        </div>
        <button
          className="btn-ghost h-9 w-9 rounded-full p-0"
          onClick={close}
          aria-label="Close trade panel"
        >
          <Icon.X size={16} />
        </button>
      </header>

      <div className="flex flex-col gap-4 p-4">
        <Segmented<"buy" | "sell">
          options={[
            { value: "buy", label: "Buy" },
            { value: "sell", label: "Sell" },
          ]}
          value={tab}
          onChange={setTab}
        />

        <div className="grid grid-cols-2 gap-2">
          {market.outcomes.slice(0, 2).map((o) => {
            const isYes = o.id === market.outcomes[0]?.id;
            const active = o.id === outcome.id;
            return (
              <button
                key={o.id}
                className={`btn-yn ${isYes ? "yes" : "no"}`}
                style={
                  active
                    ? {
                        borderColor: isYes
                          ? "rgba(46,230,168,0.7)"
                          : "rgba(255,107,107,0.7)",
                        background: isYes
                          ? "rgba(46,230,168,0.1)"
                          : "rgba(255,107,107,0.1)",
                      }
                    : { opacity: 0.55 }
                }
                onClick={() => useStore.getState().openTrade(market, o.id, tab)}
              >
                <span>{o.label}</span>
                <span className="font-mono">
                  {(isYes ? mid : 1 - mid) * 100 < 0.1
                    ? "—"
                    : ((isYes ? mid : 1 - mid) * 100).toFixed(1) + "¢"}
                </span>
              </button>
            );
          })}
        </div>

        <Segmented<OrderType>
          options={[
            { value: "market", label: "Market" },
            { value: "limit", label: "Limit" },
          ]}
          value={orderType}
          onChange={setOrderType}
        />

        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
            Size (shares)
          </span>
          <input
            type="number"
            min={1}
            step={1}
            value={size}
            onChange={(e) => setSize(Math.max(1, Number(e.target.value || 0)))}
            className="input font-mono"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          {QUICK_SIZES.map((s) => (
            <button
              key={s}
              className="btn btn-ghost !border-line"
              onClick={() => setSize(s)}
            >
              {s}
            </button>
          ))}
        </div>

        {orderType === "limit" && (
          <div>
            <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted">
              <span>Limit price</span>
              <span className="font-mono normal-case tracking-normal text-text-2">
                {limitPct}¢
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={99}
              step={1}
              value={limitPct}
              onChange={(e) => setLimitPct(Number(e.target.value))}
              className="pl-range w-full"
              style={{
                ["--val" as string]: `${limitPct}%`,
              } as React.CSSProperties}
            />
            <div className="mt-2">
              <Segmented<TimeInForce>
                options={[
                  { value: "gtc", label: "GTC" },
                  { value: "ioc", label: "IOC" },
                  { value: "fok", label: "FOK" },
                ]}
                value={tif}
                onChange={setTif}
              />
            </div>
          </div>
        )}

        <div className="rounded-card border border-line bg-bg-2 p-3 text-[12.5px]">
          <SummaryRow label="Cost" value={fmtUSD(notional, { compact: false })} />
          <SummaryRow label="Fee (est. 2%)" value={fmtUSD(fee, { compact: false })} />
          <SummaryRow label="Max payout" value={fmtUSD(payout, { compact: false })} />
          <SummaryRow label="Potential profit" value={fmtUSD(profit, { compact: false })} positive={profit > 0} />
          <SummaryRow label="ROI" value={`${roi.toFixed(0)}%`} muted />
        </div>

        {error && (
          <Pill variant="no" className="!text-[11px]">
            {error}
          </Pill>
        )}

        <button
          className="btn btn-primary w-full !py-3 !text-[14px]"
          disabled={submitting || size <= 0}
          onClick={submit}
        >
          {submitting
            ? "Placing…"
            : authReady
              ? `${tab === "buy" ? "Buy" : "Sell"} ${size} ${outcome.label}`
              : "Connect wallet to trade"}
        </button>
      </div>
    </>
  );
}

function SummaryRow({
  label,
  value,
  positive,
  muted,
}: {
  label: string;
  value: string;
  positive?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-[3px]">
      <span className="text-muted">{label}</span>
      <span
        className="font-mono"
        style={{
          color: positive ? "var(--yes)" : muted ? "var(--muted)" : "var(--text)",
        }}
      >
        {value}
      </span>
    </div>
  );
}
